import React from 'react';
import { renderRoutes } from 'react-router-config';
import { PathGeneratorContextProvider, coreRouterApi, createRoutesDict } from '@cp/entities/clientWebRoutes';
import { ROUTES_DEFAULT_CITY_ALIAS } from '@common/const/routes/defaultCity';
import { ClientWebRouteViewModel } from '@common/model/meta/clientWebRouteViewModel';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { pageIdToRouteData } from 'src/app/routes/page';
import { NotificationsSubscribeReminder } from 'src/features/notifications';
import { useIsEmbedded } from 'src/shared/mediaQueries';
import { useScrollToTop } from './shared/hooks/useScrollToTop';
import { createRoutesConfig } from './routes/config';
import { useCookieStorageCleaner } from './shared/hooks/useClientStorageCleaner';
import { ModalProvider } from './shared/components/modalProvider';
import { useRedirectToClientCity } from './shared/hooks/useRedirectToClientCity';
import { Providers, ProvidersProps } from './providers';
import { createUseAppPathGenerator } from './useAppPathGenerator';
import { AppModalManager } from './providers/modal';

type AppProps = ProvidersProps;

const Effects: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();
  const isEmbedded = useIsEmbedded();

  useCookieStorageCleaner();
  useScrollToTop();
  useRedirectToClientCity();

  const effects = isLoggedIn && isEmbedded ? <NotificationsSubscribeReminder /> : null;

  return effects;
};

export const App: React.FC<AppProps> = ({ ssrMatchMedia }) => {
  const { data: routesResponse } = coreRouterApi.useGetClientWebRoutesQuery();
  const routesRef = React.useRef<ClientWebRouteViewModel[]>([]);

  const { routesConfig, routesDict } = React.useMemo(() => {
    if (routesResponse) {
      routesRef.current = routesResponse;
    }

    return {
      routesConfig: createRoutesConfig(routesRef.current, { defaultCityAlias: ROUTES_DEFAULT_CITY_ALIAS, pageIdToRouteData }),
      routesDict: createRoutesDict(routesRef.current),
    };
  }, [routesResponse]);

  const useAppPathGenerator = createUseAppPathGenerator(routesDict);

  return (
    <Providers ssrMatchMedia={ssrMatchMedia}>
      <PathGeneratorContextProvider value={useAppPathGenerator}>
        <Effects />

        {renderRoutes([routesConfig])}

        <ModalProvider />
        <AppModalManager />
      </PathGeneratorContextProvider>
    </Providers>
  );
};
